import React, { useState, useEffect } from "react";
import { Box, Typography, TableHead, TableRow, TableCell } from "@mui/material";

import AppTable from "../../common/app-table";
import ButtonOne from "../../common/normal-button";

import { ColorsConstants, grayFontColor, grayPrimaryFontColor } from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

import { useAuth } from "../../hooks/AuthProvider";
import { RCS_RATES_ENDPOINT } from "../../config/urls";
import { getCurrencySymbol } from "../../utils/currency";

// Canonical billing groups, same keys/labels as the RCS usage table.
const GROUPS = [
  { key: "basic",        label: "BASIC" },
  { key: "single",       label: "SINGLE" },
  { key: "p2a",          label: "P2A" },
  { key: "conversation", label: "CONVERSATION" },
];

// Rate regions per card: India bots price India vs International; ROW bots have one flat card.
const CARD_CONFIG = {
  INDIA: {
    title: "India bots",
    firstColumn: "Bot type",
    regions: [
      { key: "IN",   label: "India" },
      { key: "INTL", label: "International" },
    ],
  },
  ROW: {
    title: "Rest of World bots",
    firstColumn: "Carrier",
    regions: [{ key: "ROW", label: null }],
  },
};

const thSx = {
  bgcolor: ColorsConstants.primaryBackgroundColor,
  color: ColorsConstants.primaryWhite,
  ...typography.bodyLarge,
  fontWeight: 500,
  whiteSpace: "nowrap",
  padding: "14px 16px",
  borderBottom: "none",
};
const divider = { borderRight: `1px solid ${ColorsConstants.primaryBlue90}` };

const RateCardHead = ({ config, currencySymbol }) => {
  const grouped = config.regions.length > 1;
  return (
    <TableHead>
      {grouped && (
        <TableRow>
          <TableCell rowSpan={2} sx={{ ...thSx, ...divider }}>{config.firstColumn}</TableCell>
          {config.regions.map((r, i) => (
            <TableCell
              key={r.key}
              colSpan={GROUPS.length}
              align="center"
              sx={{ ...thSx, borderBottom: `1px solid ${ColorsConstants.primaryBlue90}`, ...(i < config.regions.length - 1 ? divider : {}) }}
            >
              {r.label} ({currencySymbol.trim()})
            </TableCell>
          ))}
        </TableRow>
      )}
      <TableRow>
        {!grouped && <TableCell sx={{ ...thSx, ...divider }}>{config.firstColumn}</TableCell>}
        {config.regions.map((r, ri) =>
          GROUPS.map((g, gi) => (
            <TableCell
              key={`${r.key}-${g.key}`}
              sx={{ ...thSx, ...(grouped && gi === GROUPS.length - 1 && ri < config.regions.length - 1 ? divider : {}) }}
            >
              {g.label}{!grouped && g.key === "basic" ? " (per segment)" : ""}{!grouped ? ` (${currencySymbol.trim()})` : ""}
            </TableCell>
          ))
        )}
      </TableRow>
    </TableHead>
  );
};

const RCSRateCard = () => {
  const { getValidToken } = useAuth();
  const [data, setData]       = useState({ currency: "", cards: [] });
  const [loading, setLoading] = useState(true);

  const currencySymbol = getCurrencySymbol(data.currency);
  const fmt = (c) => {
    if (!c || c.source === "none" || c.price === null) return "—";
    return `${currencySymbol}${Number(c.price).toFixed(5)}`;
  };

  useEffect(() => {
    const load = async () => {
      try {
        const token = await getValidToken();
        const response = await fetch(RCS_RATES_ENDPOINT, {
          headers: {
            "Content-Type": "application/json",
            "X-Tenant-Id": localStorage.getItem("tenant"),
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) throw new Error("Failed to fetch RCS rates");
        const json = await response.json();
        setData({ currency: json.currency || "", cards: Array.isArray(json.cards) ? json.cards : [] });
      } catch (error) {
        console.error("Error fetching RCS rates:", error);
        setData({ currency: "", cards: [] });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const columnsFor = (config) => [
    { id: "label", label: config.firstColumn, minWidth: 180, render: (row) => row.label },
    ...config.regions.flatMap((r) =>
      GROUPS.map((g) => ({ id: `${r.key}-${g.key}`, label: g.label, render: (row) => fmt(row.cells?.[r.key]?.[g.key]) }))
    ),
  ];

  const handleExport = () => {
    const lines = [["Bot region", "Row", "Rate region", ...GROUPS.map((g) => `${g.label} (${data.currency})`)]];
    data.cards.forEach((card) => {
      const config = CARD_CONFIG[card.bot_region];
      if (!config) return;
      card.rows.forEach((row) => {
        config.regions.forEach((r) => {
          const cells = row.cells?.[r.key] || {};
          lines.push([
            config.title,
            `"${row.label}"`,
            r.label || "All numbers",
            ...GROUPS.map((g) => (cells[g.key]?.price === null || cells[g.key]?.price === undefined ? "" : Number(cells[g.key].price).toFixed(5))),
          ]);
        });
      });
    });
    const blob = new Blob([lines.map((l) => l.join(",")).join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `rcs_rate_card_${data.currency || "rates"}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const cards = data.cards.filter((card) => CARD_CONFIG[card.bot_region]);

  return (
    <Box sx={{ pb: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
        <ButtonOne startIcon={<FileDownloadOutlinedIcon />} onClick={handleExport} disabled={loading || cards.length === 0}>
          Export
        </ButtonOne>
      </Box>

      {!loading && cards.length === 0 && (
        <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>No RCS rates found.</Typography>
      )}

      {cards.map((card) => {
        const config = CARD_CONFIG[card.bot_region];
        const columns = columnsFor(config);
        return (
          <Box key={card.bot_region} sx={{ mb: 4 }}>
            <Typography sx={{ ...typography.bodyLarge, color: grayPrimaryFontColor, fontWeight: 500, mb: 1 }}>
              {config.title}
            </Typography>
            <AppTable
              columns={columns}
              rows={card.rows}
              rowKey={(row, index) => `${card.bot_region}-${index}`}
              loading={loading}
              emptyMessage="No RCS rates found."
              maxHeight="none"
              autoAdjustHeight={false}
              customHead={<RateCardHead config={config} currencySymbol={currencySymbol} />}
              customHeadColSpan={columns.length}
            />
          </Box>
        );
      })}
    </Box>
  );
};

export default RCSRateCard;
